import * as React from "react";
import { cn } from "../lib/cn";

export interface InlineAIToolbarProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Whether the popover is currently visible. Drives the brand show/hide animation. */
  open?: boolean;
  /** Absolute position in viewport pixels (typically derived from selection rect). */
  position?: { top: number; left: number };
  /** Toolbar action buttons (Improve, Shorten, Translate, Continue, …). */
  children?: React.ReactNode;
}

/**
 * InlineAIToolbar — STEP E on the v18 mockup.
 *
 * Floating dark pill that appears above the user's text selection inside
 * an `ArtifactView` viewer. ChatGPT / Claude-pattern: select text →
 * popover shows with AI actions (Improve, Shorten, Continue, …) anchored
 * at the selection top.
 *
 * Scaffolding only — the actual positioning logic (range bounding box,
 * shift-on-edge, hide on outside click) lives in the feature module
 * (`apps/client/src/features/canvas/components/InlineAIToolbar/`).
 */
export const InlineAIToolbar = React.forwardRef<HTMLDivElement, InlineAIToolbarProps>(
  ({ open = false, position, className, style, children, ...rest }, ref) => (
    <div
      ref={ref}
      role="toolbar"
      aria-hidden={!open}
      className={cn(
        "fixed z-50 flex items-center gap-0 rounded-md bg-ink-1 p-1 text-white",
        "shadow-[0_4px_12px_-2px_rgba(0,0,0,0.15),0_1px_2px_rgba(0,0,0,0.08),inset_0_1px_0_rgba(255,255,255,0.06)]",
        "transition-[opacity,transform] duration-normal ease-spring-out",
        open
          ? "pointer-events-auto translate-y-0 scale-100 opacity-100"
          : "pointer-events-none translate-y-1 scale-[0.96] opacity-0",
        className
      )}
      style={
        position
          ? { top: position.top, left: position.left, ...style }
          : style
      }
      {...rest}
    >
      {children}
    </div>
  )
);
InlineAIToolbar.displayName = "InlineAIToolbar";
